import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "@src/App";
import { autoHypenPhone, chkPhoneTel, chkEmail } from "@src/utils";
import checkIcon from "@assets/images/icon/ic-checkmark-circle-green500.svg";
import { ReactComponent as ErrorIcon } from "@src/assets/images/icon/ic-error-red700.svg";
import PostCode from "@src/components/common/postcode/PostCode";
import CustomHeader from "@src/components/common/appHeader/CustomHeader";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setUserInfo,
  weddingHallContract,
  setAdressInfo,
} from "@src/@slice/weddingHallContractSlice";
import CancelReservationModal from "@src/components/weddingHall/reservation/CancelReservationModal";
import useUrlQueryString from "@src/utils/hooks/useUrlQueryString";
import { PartnerIdContext } from "@src/routes/WeddinghallDetail";
import { Blank } from "@src/shared/src";

const HallReservation = () => {
  const { partnerId, slotId } = useContext(PartnerIdContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = useUrlQueryString();
  const partnerUuid = useParams().uuid;
  const step = query.get("step") || "0";
  const user = useContext(UserContext); //로그인한 회원 정보
  const userData = useSelector(weddingHallContract).data.user; // 실시간 계약자 정보

  //로그인 정보를 기반으로 신랑신부 정보 자동 입력되도록
  const brideAndGroomInfo = (() => {
    const isBride = user.me.sex === 1;
    return {
      brideName: (isBride ? user.me.customerName : user.me.mateName) || "",
      brideTel: (isBride ? user.me.tel : user.me.mateTel) || "",
      groomName: (isBride ? user.me.mateName : user.me.customerName) || "",
      groomTel: (isBride ? user.me.mateTel : user.me.tel) || "",
    };
  })();

  const [activeBtn, setActiveBtn] = useState(false);
  const [step1Forms, setStep1Forms] = useState({
    contractor: {
      label: "계약자 이름*",
      value: userData.contractor || user.me.customerName || "",
      readOnly: true,
    },
    contractorTel: {
      label: "계약자 전화번호*",
      value: userData.contractorTel || user.me.tel || "",
      readOnly: true,
    },
    contractorEmail: {
      label: "계약자 이메일*",
      value: userData.contractorEmail || user.me.loginId || "",
    },
    contractorAddress1: { label: "계약자 주소*", value: "" },
  });

  const [step2Forms, setStep2Forms] = useState({
    brideName: {
      label: "신부 이름*",
      value: userData.brideName || brideAndGroomInfo.brideName || "",
    },
    brideTel: {
      label: "신부 전화번호*",
      value: userData.brideTel || brideAndGroomInfo.brideTel || user.me.tel || "",
    },
    groomName: {
      label: "신랑 이름*",
      value: userData.groomName || brideAndGroomInfo.groomName || user.me.mateName || "",
    },
    groomTel: {
      label: "신랑 전화번호*",
      value: userData.groomTel || brideAndGroomInfo.groomTel || userData.groomTel || "",
    },
  });

  const [step1Errors, setStep1Errors] = useState({
    contractor: "",
    contractorTel: "",
    contractorEmail: "",
    contractorAddress1: "",
  });

  const [step2Errors, setStep2Errors] = useState({
    brideName: "",
    brideTel: "",
    groomName: "",
    groomTel: "",
  });

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const { body } = document;

    if (body.getAttribute("scrollY")) {
      body.style.removeProperty("overflow");
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  useEffect(() => {
    const userPayload = {
      contractor: userData.contractor || step1Forms.contractor.value,
      contractorTel: userData.contractorTel || step1Forms.contractorTel.value,
      contractorEmail: userData.contractorEmail || step1Forms.contractorEmail.value,
      contractorZipcode: userData.contractorZipcode || "",
      contractorAddress1: userData.contractorAddress1 || "",
      contractorAddress2: userData.contractorAddress2 || "",
      brideName: userData.brideName || step2Forms.brideName.value,
      brideTel: userData.brideTel || step2Forms.brideTel.value,
      groomName: userData.groomName || step2Forms.groomName.value,
      groomTel: userData.groomTel || step2Forms.groomTel.value,
    };
    dispatch(setUserInfo({ data: userPayload }));
  }, []);

  const isValid = (type, value: string, compareTarget: string = null): string => {
    const koreanRegex = /^[가-힣]{2,}$/;
    const nameRegex = /^[가-힣a-zA-Z\s]{2,}$/;
    const hasSpecialChar = /[^가-힣\s]/.test(value); //특수문자
    const hasOnlyConsonantsOrVowels = /[ㄱ-ㅎㅏ-ㅣ]/.test(value);

    //신랑 전화번호가 신부 전화번호와 같을 때
    if (compareTarget === "brideTel" && type.includes("Tel")) {
      if (value === userData[compareTarget]) {
        return "동일한 전화번호 입니다. 다른 전화번호를 입력해주세요.";
      } else {
        return chkPhoneTel(value) ? "" : "올바른 전화번호를 입력해주세요.";
      }
    }
    if (type.includes("Email")) {
      return chkEmail(value) ? "" : "올바른 이메일주소를 입력해주세요.";
    }
    if (type.includes("Tel")) {
      return chkPhoneTel(value) ? "" : "올바른 전화번호를 입력해주세요.";
    }
    if (type.includes("Name")) {
      return nameRegex.test(value) ? "" : "올바른 이름을 입력해주세요.";
    }
    if (type.includes("contractor")) {
      return koreanRegex.test(value) && !hasSpecialChar && !hasOnlyConsonantsOrVowels
        ? ""
        : "올바른 이름을 입력해주세요.";
    }
  };

  const handleStep1ErrorChange = (field, errorMessage) => {
    setStep1Errors((prev) => ({ ...prev, [field]: errorMessage }));
  };

  const handleStep2ErrorChange = (field, errorMessage) => {
    setStep2Errors((prev) => ({ ...prev, [field]: errorMessage }));
  };
  const clickNextBtn = () => {
    if (activeBtn) {
      if (step === "0") {
        setStep1Forms({
          contractor: { label: "계약자 이름*", value: userData.contractor || "", readOnly: true },
          contractorTel: {
            label: "계약자 전화번호*",
            value: userData.contractorTel || "",
            readOnly: true,
          },
          contractorEmail: { label: "계약자 이메일*", value: userData.contractorEmail || "" },
          contractorAddress1: { label: "계약자 주소*", value: "" },
        });
        // setStep(1);
        navigate(`/weddinghall/${partnerUuid}/contract?step=1&id=${slotId}`);
        window.scrollTo(0, 0);
      } else if (step === "1") {
        setStep2Forms({
          brideName: { label: "신부 이름*", value: userData.brideName || "" },
          brideTel: { label: "신부 전화번호*", value: userData.brideTel || "" },
          groomName: { label: "신랑 이름*", value: userData.groomName || "" },
          groomTel: { label: "신랑 전화번호*", value: userData.groomTel || "" },
        });
        navigate(`/weddinghall/${partnerUuid}/contract/payment?id=${slotId}`);
      }
    }
  };

  useEffect(() => {
    const step1Fields = Object.keys(step1Forms).slice(0, 3);
    const step2Fields = Object.keys(step2Forms);
    const allFieldsFilled1 = Object.entries(userData)
      .filter((data) => step1Fields.indexOf(data[0]) > -1)
      .every((value) => value[1] !== "");
    const allFieldsFilled2 = Object.entries(userData)
      .filter((data) => step2Fields.indexOf(data[0]) > -1)
      .every((value) => value[1] !== "");
    const allStep1ErrorsEmpty = Object.values(step1Errors).every((msg) => msg === "");
    const allStep2ErrorsEmpty = Object.values(step2Errors).every((msg) => msg === "");
    const fillAddress = !!userData.contractorZipcode && !!userData.contractorAddress1;

    if (step === "0") {
      setActiveBtn(allFieldsFilled1 && fillAddress && allStep1ErrorsEmpty);
    } else {
      setActiveBtn(allFieldsFilled2 && allStep2ErrorsEmpty);
    }
  }, [step1Errors, step2Errors, userData, step]);

  const headerGoBack = () => {
    if (step === "0") {
      navigate(`/weddinghall/${partnerUuid}/slot?partnerid=${partnerId}&id=${slotId}`);
    } else if (step === "1") {
      navigate(`/weddinghall/${partnerUuid}/contract?step=0&id=${slotId}`, { replace: true });
    } else {
      alert(step);
    }
  };

  return (
    <div className="page-hall-reservation hide-scroll">
      <>
        <CustomHeader goBack={headerGoBack} close={() => setShowCancelModal(true)} />
        <div className="reservation-form-wrap">
          <h1 className="bold">
            {step === "0"
              ? `잔여타임 선점을 위해\n계약자 정보를 확인해주세요.`
              : `예식을 진행할\n신랑, 신부 정보를 입력해 주세요.`}
          </h1>
          {step === "0" && (
            <div className="help-txt-wrap">
              <p>
                <img src={checkIcon} alt="" width={18} />
                <span className="bold">계약자 정보 안내</span>
              </p>
              <div>
                <p>
                  <span>•</span>
                  <span>계약금 결제 시 입력한 계약자 정보가 웨딩홀에 전달됩니다.</span>
                </p>
                <p>
                  <span>•</span>
                  <span>
                    계약금 결제 후 이름이나 연락처가 변경된 경우 웨딩홀로 직접 전화해서 변경 요청해
                    주셔야 합니다.
                  </span>
                </p>
              </div>
            </div>
          )}
          <div className={`input-form-wrap${step} hide-scroll`}>
            {step === "0"
              ? Object.keys(step1Forms).map((key) => (
                  <React.Fragment key={key}>
                    {key === "contractorAddress1" ? (
                      <div className="form-block">
                        <span className="title">{step1Forms[key].label}</span>
                        <PostCode
                          updateAddress={setAdressInfo}
                          postcode={{
                            zipcode: userData.contractorZipcode,
                            roadAddress: userData.contractorAddress1,
                            detailAddress: userData.contractorAddress2,
                          }}
                        />
                      </div>
                    ) : (
                      <InputForm
                        keyName={`${key}`}
                        data={step1Forms[key]}
                        validate={(value) => isValid(key, value)}
                        onErrorChange={handleStep1ErrorChange}
                      />
                    )}
                  </React.Fragment>
                ))
              : Object.keys(step2Forms).map((key) => (
                  <InputForm
                    key={key}
                    keyName={key}
                    data={step2Forms[key]}
                    validate={(value) =>
                      key === "groomName"
                        ? isValid(key, value, "brideName")
                        : key === "groomTel"
                          ? isValid(key, value, "brideTel")
                          : isValid(key, value)
                    }
                    onErrorChange={handleStep2ErrorChange}
                  />
                ))}

            {step === "1" && <Blank height={100} />}
          </div>
        </div>

        <div className="gradient-background"> </div>
        <button
          className={`btn btn-full btn-giant ${activeBtn === false ? "btn-disabled" : "btn-red500"}`}
          onClick={clickNextBtn}
        >
          다음
        </button>

        <CancelReservationModal isShow={showCancelModal} onClose={setShowCancelModal} />
      </>
    </div>
  );
};

export default HallReservation;

const InputForm = ({ keyName, data, validate, onErrorChange }) => {
  const dispatch = useDispatch();
  const [newValue, setNewValue] = useState(data.value);
  const [error, setError] = useState("");

  // useEffect(() => setNewValue(data.value), [data.value]);

  const handleChange = (e) => {
    const updatedValue = keyName.includes("Tel") ? autoHypenPhone(e.target.value) : e.target.value;
    setNewValue(updatedValue);
    dispatch(setUserInfo({ data: { [keyName]: updatedValue } }));

    if (validate) {
      const validationError = validate(updatedValue);
      setError(validationError);
      onErrorChange(keyName, validationError);
    }
  };

  return (
    <div className="form-block">
      <span className="title">{data.label}</span>
      <input
        type={keyName.includes("Tel") ? "tel" : "text"}
        className={`input-text input-full`}
        value={newValue}
        onChange={handleChange}
        onBlur={handleChange}
        disabled={!!data.readOnly}
      />
      {error && (
        <div className="error-txt-wrap">
          <ErrorIcon />
          <p className="error-txt">{error}</p>
        </div>
      )}
    </div>
  );
};
